import {
    FC,
    MouseEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import Table from "../../assets/components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import "../../assets/components/Table/Table_header.scss";
import {
    Permissions,
    User,
    UserRoles,
    UserTable,
    renderSubComponentUserTable,
} from "../../models/models";
import {
    useCreateUserMutation,
    useDeleteUserMutation,
    useGetUsersListQuery,
    useUpdateUserMutation,
} from "../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import Loader from "../../assets/components/Loader/Loader";
import RemoveButton from "../../assets/components/RemoveButton/RemoveButton";
import UsersSubRow from "./UsersSubRow/UsersSubRow";
import TableHeader from "../../assets/components/Table/TableHeader";
import { useNotification } from "../../assets/hooks/useNotification";
import { actionText } from "../../text";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import { hasPermission } from "../../assets/helpers/helpers";
import { toast } from "react-toastify";

type Removed = {
    userId: number;
};

const RemoveUserButton: FC<Removed> = ({ userId }) => {
    const [deleteUser] = useDeleteUserMutation({
        fixedCacheKey: "deleted_user",
    });

    const { AttentionMessageBox } = useNotification();

    const deleteUserHandler = () =>
        AttentionMessageBox({ textContent: actionText("delete", "user") }).then(
            (value) => {
                if (value === "yes") deleteUser(userId);
            }
        );

    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => deleteUserHandler()}
        />
    );
};

const buildColumns = (canManageUsers: boolean): ColumnDef<User>[] => [
    {
        header: "Name",
        accessorKey: "username",
        cell: ({ row, getValue }) => {
            return row.original.id !== -1 ? getValue<string>() : "Add New User";
        },
    },
    {
        header: "Role",
        accessorKey: "role",
    },
    {
        id: "expander",
        header: () => null,
        cell: ({ row }) => {
            return row.original.id !== -1 &&
                canManageUsers &&
                row.original.id !== 0 &&
                row.original.id !== 1 ? (
                <button
                    className="edit_button"
                    {...{
                        onClick: () => row.getToggleExpandedHandler(),
                        style: { cursor: "pointer" },
                    }}
                >
                    <span>Edit</span>
                </button>
            ) : null;
        },
    },
    {
        id: "removed",
        header: () => null,
        cell: ({ row }) => {
            return row.original.id !== -1 &&
                canManageUsers && (
                    <RemoveUserButton userId={row.original.id} />
                )
        },
    },
];

const renderSubComponent: renderSubComponentUserTable<UserRoles> = (props) => {
    const closeFormHandler = (id: number) => {
        props.row.toggleExpanded();

        if (id === -1 && props.subRowHandler !== undefined) {
            props.subRowHandler(id);
        }
    };

    return (
        <UsersSubRow
            {...props.row.original}
            closeFormHandler={closeFormHandler}
        />
    );
};

const Users: FC = () => {
    const dispatch = useAppDispatch();
    const [isNewUserFormVisible, setNewUserFormVisible] = useState(false);
    const [dataTable, setData] = useState<User[]>([]);
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const permissions = useAppSelector(
        (state) => state.initializeApp.permissions
    );
    const canManageUsers = hasPermission(
        permissions,
        Permissions.USER_MANAGE_ACCESS
    );
    const columns = useMemo(() => buildColumns(canManageUsers), [canManageUsers]);

    const { data: dataUser, isLoading: isLoadingUser, isSuccess: isSuccessUser, isError: isErrorUser } = useGetUsersListQuery("", {
        refetchOnFocus: true,
    });

    const [, {
        isError: isErrorUpdateUser,
        isSuccess: isSuccessUpdateUser,
        isLoading: isLoadingUpdateUser,
        reset: resetUpdateUser,
    }] = useUpdateUserMutation({
        fixedCacheKey: "updated_user"
    });

    const [, {
        isError: isErrorCreateUser,
        isSuccess: isSuccessCreateUser,
        isLoading: isLoadingCreateUser,
        reset: resetCreateUser,
    }] = useCreateUserMutation({
        fixedCacheKey: "created_user"
    });

    const [, {
        isError: isErrorDeletedUser,
        isSuccess: isSuccessDeletedUser,
        isLoading: isLoadingDeletedUser,
        reset: resetDeletedUser,
    }] = useDeleteUserMutation({
        fixedCacheKey: "deleted_user",
    });

    const handleUsersData = useCallback(() => {
        if (isSuccessUser && dataUser) {
            setData(dataUser);

        } else if (isErrorUser) {
            toast.error("Error loading users data");
        }
        setNewUserFormVisible(false);
    }, [dataUser, isErrorUser, isSuccessUser]);

    const handleCreateUser = useCallback(() => {
        if (isSuccessCreateUser) {
            toast.success("User created successfully");
            resetCreateUser();
        } else if (isErrorCreateUser) {
            toast.error("Error creating user");
        }
    }, [isErrorCreateUser, isSuccessCreateUser, resetCreateUser]);

    const handleUpdateUser = useCallback(() => {
        if (isSuccessUpdateUser) {
            toast.success("User updated successfully");
            resetUpdateUser();
        } else if (isErrorUpdateUser) {
            toast.error("Error updating user");
        }
    }, [isErrorUpdateUser, isSuccessUpdateUser, resetUpdateUser]);

    const handleDeleteUser = useCallback(() => {
        if (isSuccessDeletedUser) {
            toast.success("User deleted successfully");
            resetDeletedUser();
        } else if (isErrorDeletedUser) {
            toast.error("Error deleting user");
        }
    }, [isErrorDeletedUser, isSuccessDeletedUser, resetDeletedUser]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingCreateUser ||
            isLoadingUser ||
            isLoadingUpdateUser ||
            isLoadingDeletedUser;
        dispatch(uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden"));
    }, [dispatch, isLoadingCreateUser, isLoadingDeletedUser, isLoadingUpdateUser, isLoadingUser]);


    useEffect(() => {
        handleLoadingState();
        handleCreateUser();
        handleUpdateUser();
        handleUsersData();
        handleDeleteUser();
    }, [
        handleCreateUser,
        handleLoadingState,
        handleUsersData,
        handleUpdateUser,
        handleDeleteUser,
    ]);

    const createNewUserHandler: MouseEventHandler<HTMLButtonElement> = () => {
        const newRow: User = {
            id: -1,
            username: "UserName",
            role: "admin",
            isOpen: true,
        };

        if (!isNewUserFormVisible) {
            setData((prevData) => [newRow, ...prevData]);
            setNewUserFormVisible(!isNewUserFormVisible);
        }
    };

    const removeNewUserHandler = (id: number) => {
        const data = dataTable.filter((user) => user.id !== id);
        setData(data);
        setNewUserFormVisible(!isNewUserFormVisible);
    };

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="Users" />
            {dataTable?.length ? (
                <div className="table_scroll_wrapper">
                    <TableHeader
                        title="List of users"
                        onAddRowHandler={createNewUserHandler}
                        addPermission={Permissions.USER_MANAGE_ACCESS}
                    />
                    <Table<UserTable, UserRoles>
                        className="users_page"
                        data={dataTable}
                        columns={columns}
                        getRowCanExpand={() => canManageUsers}
                        subRowHandler={removeNewUserHandler}
                        renderSubComponent={renderSubComponent}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default Users;
